import { useRef } from 'react';
import { useMobileNavigation } from '@/state/MobileNavigationContext';
import useMobileNavigationAnimation from '@/hooks/animations/useMobileNavigationAnimation';
import NavigationLink from '@/components/elements/NavigationLink';
import SocialLink from '@/components/elements/SocialLink';
import NavigationDropdown from '@/components/NavigationDropdown';
import { socialLinks } from '@/constants/navigation-items';

type NavigationItem = {
  label: string;
  path: string;
  hash?: string;
  children?: NavigationItem[];
};

interface HeaderNavigationProps {
  isMobile: boolean;
  items: NavigationItem[];
  variant?: 'top-middle' | 'nav-bar';
}

function HeaderNavigation({ items, isMobile, variant = 'nav-bar' }: HeaderNavigationProps) {
  const { isOpen, setIsOpen } = useMobileNavigation();
  const navigationRef = useRef<HTMLDivElement>(null);

  const navClasses = `
    print:hidden overflow-hidden desktopNav:overflow-visible bg-white fixed z-30 w-full 
    top-[63px] desktopNav:top-[71px] start-0 border-t border-gray-200 desktopNav:block 
	 ${isOpen ? 'block' : 'hidden'} 
    ${variant === 'top-middle' ? 'desktopNav:border-none desktopNav:w-min desktopNav:!top-[15px] desktopNav:!left-[50%] desktopNav:translate-x-[-50%]' : 'border-b border-gray-200'}
  `;

  useMobileNavigationAnimation(isOpen, isMobile, navigationRef);

  function closeMobileNavigation() {
    if (isOpen) {
      setIsOpen(false);
    }
  }

  function renderNavigationItems(items: NavigationItem[]) {
    return items.map((item) => (
      <li key={item.label}>
        <NavigationLink onClick={closeMobileNavigation} path={item.path} hash={item.hash}>
          {item.label}
        </NavigationLink>
      </li>
    ));
  }

  function renderNavigationItemsWithDropdowns(items: NavigationItem[]) {
    return items.map((item) => {
      // If this item has children and we're on desktop, render a dropdown
      if (item.children && !isMobile) {
        return (
          <li key={item.label}>
            <NavigationDropdown items={item.children} label={item.label} />
          </li>
        );
      }

      // If this item has children and we're on mobile, render a section with header
      if (item.children && isMobile) {
        return (
          <div key={item.label}>
            <li className='pt-3 pb-1 font-bold'>{item.label}</li>
            {renderNavigationItems(item.children)}
          </div>
        );
      }

      return (
        <li key={item.label}>
          <NavigationLink onClick={closeMobileNavigation} path={item.path} hash={item.hash}>
            {item.label}
          </NavigationLink>
        </li>
      );
    });
  }

  return (
    <nav
      ref={navigationRef}
      className={navClasses}
      id='header-navigation'
      data-cy='header-navigation'
      aria-hidden={isMobile ? !isOpen : false}
    >
      <div className='desktopNav:container px-4 flex flex-wrap items-center mx-auto p-2 desktopNav:py-0 print:hidden'>
        <div className='w-full block desktopNav:w-auto'>
          <ul
            className='
            max-desktopNav:[@media(max-height:700px)]:text-[0.9rem] 
            desktopNav:gap-7 flex flex-col p-4 desktopNav:p-0 mx-auto border 
				border-gray-100 rounded-lg bg-gray-50 desktopNav:flex-row 
				desktopNav:items-stretch desktopNav:space-x-0 rtl:space-x-reverse desktopNav:mt-0 
				desktopNav:border-0 desktopNav:bg-white'
          >
            {renderNavigationItemsWithDropdowns(items)}

            {isMobile && socialLinks.length > 0 && (
              <>
                <li className='pt-3 pb-1 font-bold'>Social Accounts</li>
                {socialLinks.map((link) => (
                  <li key={link.url}>
                    <SocialLink href={link.url} label={link.label} />
                  </li>
                ))}
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default HeaderNavigation;
